import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box, TextField, Typography, Paper, Divider, Stack,
  Chip,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import {
  updateWorkstreamSprint,
  removeWorkstreamSprint,
  setSprintOverride,
  selectSprintsForWorkstream,
  WorkstreamSprint,
} from '../../store/slices/sprintsSlice';
import type { WorkstreamSprintStatus } from '../../store/slices/sprintsSlice';
import ModalActionFooter from '../../components/ModalActionFooter/ModalActionFooter';
import NumberInput from '../../components/NumberInput/NumberInput';
import { formatDate, getSprintDateRange } from '../../utils/sprintDateUtils';

const SPRINT_STATUSES: { value: WorkstreamSprintStatus | ''; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'Active', label: 'Active' },
  { value: 'Closed', label: 'Closed' },
];

const SprintEditView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { workstreamId, sprintId } = useParams<{ workstreamId: string; sprintId: string }>();
  const dispatch = useAppDispatch();

  const from = (location.state as { from?: string } | null)?.from;

  const workstream = useAppSelector((state) =>
    state.workstreams.workstreams.find((w) => w.id === workstreamId),
  );
  const sprints = useAppSelector((state) =>
    workstreamId ? selectSprintsForWorkstream(state, workstreamId) : [],
  );
  const sprint = sprints.find((s) => s.id === sprintId);
  const pods = useAppSelector((state) => state.pods.pods);
  const sprintsConfig = useAppSelector((state) => state.sprints);

  const selectedPodIds = workstream?.entries[0]?.podIds ?? [];
  const selectedPods = pods.filter((p) => selectedPodIds.includes(p.id));
  const defaultVelocity = selectedPods.reduce((sum, p) => sum + p.defaultVelocity, 0);

  const existingOverride = sprintsConfig.overrides.find(
    (o) => o.workstreamId === workstreamId && o.sprintIndex === (sprint?.number ?? 0) - 1,
  );

  const [nickname, setNickname] = useState('');
  const [velocity, setVelocity] = useState(defaultVelocity);
  const [status, setStatus] = useState<WorkstreamSprintStatus | ''>('');

  useEffect(() => {
    if (sprint) {
      setNickname(sprint.nickname ?? '');
      setStatus(sprint.status ?? '');
    }
  }, [sprint]);

  useEffect(() => {
    setVelocity(existingOverride?.velocity ?? defaultVelocity);
  }, [existingOverride, defaultVelocity]);

  const handleBack = () => {
    if (from === '/workstream-forecaster') {
      navigate('/workstream-forecaster');
    } else {
      navigate(`/epic-planner/workstreams/${workstreamId}/edit`);
    }
  };

  const handleSave = () => {
    if (!sprint || !workstreamId) return;
    const updatedSprint: WorkstreamSprint = {
      ...sprint,
      nickname: nickname.trim() || undefined,
      status: status || undefined,
    };
    dispatch(updateWorkstreamSprint(updatedSprint));
    dispatch(setSprintOverride({
      workstreamId,
      sprintIndex: sprint.number - 1,
      velocity,
      memberCapacities: existingOverride?.memberCapacities ?? {},
    }));
    handleBack();
  };

  const handleConfirmDelete = () => {
    if (!sprint || !workstreamId) return;
    dispatch(removeWorkstreamSprint({ workstreamId, sprintId: sprint.id }));
    navigate(`/epic-planner/workstreams/${workstreamId}/edit`);
  };

  if (!workstream || !sprint) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Sprint not found.</Typography>
      </Box>
    );
  }

  const dateRange = getSprintDateRange(sprintsConfig.startDate, sprintsConfig.sprintLength, sprint.number);
  const sprintLabel = `Sprint ${sprint.number}${sprint.nickname ? ` · ${sprint.nickname}` : ''}`;
  const hasVelocityOverride = existingOverride !== undefined;

  return (
    <Box sx={{ maxWidth: 680, mx: 'auto' }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <EventNoteIcon color="action" />
          <Typography variant="h5" fontWeight={600}>
            {sprintLabel}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {workstream.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            label={`${formatDate(dateRange.start)} – ${formatDate(dateRange.end)}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`${velocity} pts`}
            size="small"
            color={hasVelocityOverride ? 'warning' : 'default'}
            variant={hasVelocityOverride ? 'outlined' : 'filled'}
          />
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>
          {/* Nickname */}
          <TextField
            label="Nickname (optional)"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="e.g. Kickoff, Hardening…"
            fullWidth
          />

          {/* Velocity override */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Velocity Override</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NumberInput value={velocity} onChange={setVelocity} min={0} />
              <Typography variant="caption" color="text.secondary">
                pts (default: {defaultVelocity})
              </Typography>
            </Box>
          </Box>

          {/* Status */}
          <FormControl fullWidth size="small">
            <InputLabel id="sprint-status-label">Status</InputLabel>
            <Select
              labelId="sprint-status-label"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as WorkstreamSprintStatus | '')}
            >
              {SPRINT_STATUSES.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Box sx={{ mt: 4 }}>
          <ModalActionFooter
            onSave={handleSave}
            saveLabel="Save"
            onCancel={handleBack}
            onDelete={handleConfirmDelete}
            deleteLabel="Delete Sprint"
            deleteConfirmMessage={`Are you sure you want to delete ${sprintLabel}? Stories assigned to it will be unlinked from this sprint.`}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default SprintEditView;
